import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import { PlantApi } from 'apis/PlantApis';
import { SaleLineApi } from 'apis/SaleLineApi';
import SaleLineTeamApis, {
  ICheckExistSaleTeamRoute,
  ICreateSaleTeamRoute,
} from 'apis/SaleLineTeamApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { initLazySelectData } from 'components/Select/LazySelectForm';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_SALES_TEAM_ROUTE } from 'utils/constants/importId';
import FilterSaleLineTeam from './FilterSaleLineTeam';
import TableSaleLineTeam from './TableSaleLineTeam';

interface ValidItem {
  id: string;
}

const { PlusOutlined } = icons;

const SaleLineTeam = () => {
  const navigate = useNavigate();

  const { user } = useUser(),
    { setTitle, setBreadcrumb } = useTitle();

  const childRef = useRef<any>(null);
  const filterRef = useRef<any>(null);
  const firstRender = useRef(true);

  const [edit, setEdit] = useState(-1),
    [isOpenFilter, setIsOpenFilter] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const [data, setData] = useState<any>([]),
    [pageInfo, setPageInfo] = useState<any>([]),
    [routeData, setRouteData] = useState<{ options: any; totalItems: number }>(
      initLazySelectData,
    );

  const [dataPlant, setDataPlant] = useState<any>([{}]),
    [dataSearch, setDataSearch] = useState<any>([{}]);

  const defaultFilter = useMemo(() => {
    if (user?.isSale) return { userId: user.id, plant: user.plantCode };
    if (user?.isCN) return { plant: user.plantCode };
    return {};
  }, [user]);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const {
      isPending: getSaleLineTeamLoading,
      mutate: getSaleLineTeams,
      data: getSaleLineSalesTeamMutateDate,
    } = useMutation({
      mutationFn: (params: {
        export?: { headerRow: ColumnsTypeCustom };
        page?: number;
        page_size?: number;
        input?: any;
      }) => SaleLineTeamApis.getSaleLineTeamApis(params),
      onSuccess: (res: any, variables) => {
        if (res?.data?.getFteamRouteTrip) {
          const data = res.data.getFteamRouteTrip.items.map((item: any) => ({
            ...item,
            ttripID: item.tripId,
            ttripName: item.tripName,
            tteamSalesID: item.teamSaleId,
            tteamSalesName: item.teamSaleName,
            trouteID: item.routeId,
            trouteName: item.routeName,
            plantID: item.plantId,
            tcreateUser: item.createName !== null ? item.createName : '',
            tmodifyUser: item.modifyName !== null ? item.modifyName : '',
          }));

          if (typeof variables.export !== 'undefined') {
            exportExcel_v2(
              variables.export.headerRow,
              data,
              'Kế hoạch - Tuyến đội bán hàng',
            );

            return;
          }

          setData(data);
          setPageInfo(res.data.getFteamRouteTrip.pageInfo);
        }

        if (res?.errors) {
          toastError(res.errors[0].message);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: getPlantLoading, mutate: getPlant } = useMutation({
      mutationFn: () => PlantApi.getAllPlant(),
      onSuccess: (res) => {
        setDataPlant(res.data.getPlant.items);
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: isGetRoutePending, mutate: getRoute } = useMutation({
      mutationFn: (params: ISearchRouteComboBox) =>
        SaleLineApi.getAllRouteComboBox({ ...params, statusId: 'R100_01' }),
      onSuccess: (res) => {
        if (res?.data?.getRouteM)
          setRouteData({
            options: res.data.getRouteM?.items ?? [],
            totalItems: res.data.getRouteM?.pageInfo?.totalItems ?? 0,
          });
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: updateSaleLineTeamLoading, mutate: updateSaleLineTeam } =
      useMutation({
        mutationFn: (data: any) => SaleLineTeamApis.updateSaleLineTeam(data),
        onSuccess: (res: any) => {
          if (res?.data?.updateTeamSalesRoute) {
            if (typeof user === 'undefined') return;

            if (
              dataSearch?.plant === undefined &&
              dataSearch?.line === undefined &&
              dataSearch?.team === undefined
            ) {
              getSaleLineTeams({
                page: page,
                page_size: page_size,
                input: {
                  plantId: defaultFilter?.plant ?? user?.plantCode,
                },
              });
            } else {
              getSaleLineTeams({
                page: page,
                page_size: page_size,
                input: {
                  teamId: dataSearch.team,
                  routeId: dataSearch.line,
                  plantId: dataSearch?.plant ?? user?.plantCode,
                },
              });
            }
            toastSuccess('Cập nhật thành công');
            return;
          }

          if (res?.errors)
            toastError(`Cập nhật thất bại. ${res.errors[0].message}`);
        },
        onError: (error) => {
          toastError('Cập nhật thất bại');
          console.log(error);
        },
      }),
    {
      mutate: handleCheckValidSalesTeamAndRoute,
      isPending: isCheckValidSaleTeamPending,
    } = useMutation({
      mutationFn: ({
        saleTeam,
      }: {
        inValidMessage: {
          saleTeam: string;
          route: string;
        };
        saleTeam: ValidItem;
        route: ValidItem;
        onValid: () => void;
      }) => SaleTeamApi.checkValidSaleTeamById({ id: saleTeam.id }),
      onSuccess: (res, variables) => {
        const saleTeam = res?.data?.getTeamSales?.items || [];
        const { route, inValidMessage, onValid } = variables;

        if (saleTeam.length > 0)
          checkValidRouteById({
            route,
            inValidMessage: inValidMessage.route,
            onValid: onValid,
          });
        else toastError(inValidMessage.saleTeam);
      },
      onError: (error) => {
        toastError(error.message);
      },
    }),
    { mutate: checkValidRouteById, isPending: isCheckValidRouteByIdPending } =
      useMutation({
        mutationFn: ({
          route,
        }: {
          inValidMessage: string;
          route: ValidItem;
          onValid: any;
        }) => SaleLineApi.checkValidRouteById({ id: route.id }),
        onSuccess: (res, variables) => {
          try {
            const route = res?.data?.getRouteM?.items || [];
            const { inValidMessage, onValid } = variables;

            if (route.length > 0) typeof onValid === 'function' && onValid();
            else toastError(inValidMessage);

            // if (route.length > 0) {
            //   const [item] = route;
            //   const validToDate = dayjs(item?.validToDate).isValid()
            //     ? dayjs(item?.validToDate)
            //     : null;
            //   const currentDate = dayjs();

            //   if (
            //     validToDate &&
            //     validToDate.startOf('day').isAfter(currentDate.startOf('day'))
            //   )
            //     typeof onValid === 'function' && onValid();
            //   else toastError(inValidMessage);
            // } else toastError(inValidMessage);
          } catch (error: any) {
            toastError(error);
          }
        },
        onError: (error) => {
          toastError(error.message);
        },
      }),
    {
      mutate: handleCreateTeamSaleRoute,
      isPending: isHandleCreateTeamSaleRoutePending,
    } = useMutation({
      mutationFn: ({
        params,
      }: {
        params: {
          checkExist: ICheckExistSaleTeamRoute;
          createTeamSaleRoute: ICreateSaleTeamRoute;
        };
        onCreateTeamSaleRouteSuccess?: () => void;
      }) => SaleLineTeamApis.checkExistSaleTeamRoute(params.checkExist),
      onSuccess: (res: any, variables) => {
        try {
          if (res?.data?.getTeamSalesRoute?.items.length > 0)
            toastError('Tuyến đội bán hàng này đã tồn tại!');
          else if (res.data?.errors) toastError(res.data.errors[0]?.message);
          else {
            const { params, onCreateTeamSaleRouteSuccess } = variables;

            createSaleTeamRoute({
              params: params.createTeamSaleRoute,
              onSuccess: onCreateTeamSaleRouteSuccess,
            });
          }
        } catch (error) {
          console.log(error);
        }
      },
      onError: (error) => {
        console.log(error.message);
      },
    }),
    { isPending: isCreateSaleTeamRoutePending, mutate: createSaleTeamRoute } =
      useMutation({
        mutationFn: ({
          params,
        }: {
          params: ICreateSaleTeamRoute;
          onSuccess?: () => void;
        }) => SaleLineTeamApis.createSaleTeamRoute(params),
        onSuccess: (res: any, variables) => {
          if (res?.data) {
            const { onSuccess } = variables;

            toastSuccess('Tạo Tuyến đội thành công');

            if (filterRef && filterRef.current)
              filterRef.current?.handleCancelFilter();

            onSuccess && onSuccess();
          } else if (res?.errors)
            toastError(`Tạo tuyến đội thất bại. ${res?.errors[0]?.message}`);
        },
        onError: (error) => {
          toastError(`Tạo Tuyến đội thất bại. ${error?.message}`);
        },
      });

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;

      getPlant();
      getRoute({ plantId: defaultFilter.plant, pageSize: 10 });
      setTitle('Tuyến - Đội bán hàng');
      setBreadcrumb([{ title: 'Kế hoạch' }, { title: 'Tuyến - Đội bán hàng' }]);
    }

    getSaleLineTeams({
      page: page,
      page_size: page_size,
      input: {
        ...(dataSearch.team && { teamId: dataSearch.team }),
        ...(dataSearch.line && { routeId: dataSearch.line }),
        ...(defaultFilter.userId && { userId: defaultFilter.userId }),
        plantId:
          defaultFilter?.plant || dataSearch.plant
            ? defaultFilter.plant || dataSearch.plant
            : user?.plantCode,
      },
    });
  }, [page, page_size, dataSearch, defaultFilter]);

  const handleExportExcel = async () => {
    if (childRef && childRef.current) {
      getSaleLineTeams({
        page: 1,
        page_size:
          getSaleLineSalesTeamMutateDate?.data?.getFteamRouteTrip?.pageInfo
            ?.totalItems,
        export: { headerRow: childRef.current.getHeaderRow() },
        input: {
          teamId: dataSearch.team,
          routeId: dataSearch.line,
          plantId: dataSearch?.plant ?? user?.plantCode,
        },
      });
    }
  };

  const handleImportData = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const refetchRoute = useCallback(() => {
    if (filterRef && filterRef.current) filterRef.current?.handleCancelFilter();
  }, [page_size, dataSearch]);

  const handleSaveRow = useCallback(
    ({ values, onSuccess }: { values: any; onSuccess?: () => void }) => {
      const {
        id,
        useYn,
        tteamSalesID: teamSalesId,
        trouteID: routeId,
        teamSaleSubId,
        ttripID: tripId,
        plantID,
      } = values;

      if (values?.id === null) {
        const params = { routeId, teamSalesId, plantId: plantID };

        handleCheckValidSalesTeamAndRoute({
          route: { id: routeId },
          saleTeam: { id: teamSalesId },
          inValidMessage: {
            saleTeam: `Đã có lỗi xảy ra. Đội ${teamSaleSubId} đã kết thúc không thể lập tuyến đội.`,
            route: `Đã có lỗi xảy ra. Tuyến ${routeId} đã kết thúc không thể lập tuyến đội.`,
          },
          onValid: () =>
            handleCreateTeamSaleRoute({
              params: {
                checkExist: params,
                createTeamSaleRoute: params,
              },
              onCreateTeamSaleRouteSuccess: onSuccess,
            }),
        });
        return;
      }

      const updateData = { teamSalesId, routeId, tripId, useYn, id };

      if (values?.type === 'switch' && values?.useYn === true) {
        handleCheckValidSalesTeamAndRoute({
          route: { id: routeId },
          saleTeam: { id: teamSalesId },
          inValidMessage: {
            saleTeam: `Đã có lỗi xảy ra. Đội ${teamSaleSubId} đã kết thúc không thể kích hoạt tuyến - đội.`,
            route: `Đã có lỗi xảy ra. Tuyến ${routeId} đã kết thúc không thể kích hoạt tuyến - đội.`,
          },
          onValid: () => updateSaleLineTeam(updateData),
        });
        return;
      }

      updateSaleLineTeam(updateData);
    },
    [data],
  );

  const handleOkFilter = (values: any) => {
    navigate(`?page=1&page_size=${page_size}`);
    setDataSearch(values);
  };

  const handleCancel = () => {
    setDataSearch({});
  };

  const handleAddRow = () => {
    if (childRef && childRef.current) {
      setIsOpenFilter(false);
      childRef.current.handleAddRow();
    }
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            disabled={edit !== -1}
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
          />
        </Col>
        <Col>
          <ButtonExport disabled={edit !== -1} onClick={handleExportExcel} />
        </Col>
        <Col>
          <ButtonImport disabled={edit !== -1} onClick={handleImportData} />
        </Col>
        <Col>
          {user?.isHO ? (
            <></>
          ) : (
            <Button
              disabled={edit !== -1}
              fill
              iconBefore={<PlusOutlined />}
              title="Tạo"
              handleOnclick={handleAddRow}
            />
          )}
        </Col>
      </Row>
      <UploadModal
        importId={IMPORT_SALES_TEAM_ROUTE}
        templateFileName="Kế hoạch - Tuyến - đội bán hàng - Template"
        open={isOpenImportModal}
        title={'Thêm tuyến - đội bán hàng'}
        setOpen={setIsOpenImportModal}
        refetch={refetchRoute}
      />
      <FilterSaleLineTeam
        plants={dataPlant}
        saleLines={routeData.options}
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        ref={filterRef}
        onFinish={handleOkFilter}
        onCancel={handleCancel}
      />
      <TableSaleLineTeam
        page={page}
        pageSize={page_size}
        data={data}
        pageInfo={pageInfo}
        edit={edit}
        ref={childRef}
        plantData={dataPlant}
        route={{
          data: routeData,
          isPending: isGetRoutePending,
          mutate: getRoute,
        }}
        loading={
          getSaleLineTeamLoading ||
          getPlantLoading ||
          updateSaleLineTeamLoading ||
          isCreateSaleTeamRoutePending ||
          isHandleCreateTeamSaleRoutePending ||
          isCheckValidSaleTeamPending ||
          isCheckValidRouteByIdPending
        }
        onSave={handleSaveRow}
        setData={setData}
        setEdit={setEdit}
      />
    </>
  );
};

export default SaleLineTeam;
